/**
 * Content Management Service
 * CRUD operations for content items (flipbooks, images, videos, files)
 * Designed for both internal CRM use and external 3rd party app access
 */

import { supabase } from "./supabase";
import { uploadFile, deleteFile, getPublicUrl } from "./storage.service";
import { processPDF, type PDFProcessingProgress } from "../utils/pdfProcessor";
import type { UUID } from "../types/common.types";
import type {
  ContentItem,
  ContentType,
  ContentFilters,
  CreateContentItemInput,
  UpdateContentItemInput,
  FlipbookDetails,
  ImageDetails,
  VideoLinkDetails,
  TextBlockDetails,
  WebpageChatDetails,
  FlipbookViewerData,
  FlipbookPage,
  VideoPlatform,
} from "../types/content.types";

// Storage bucket for all content
const CONTENT_BUCKET = "content";

/**
 * Sanitize operator-typed search terms before they are interpolated
 * into a PostgREST `.or()` clause. Allow-list matches the
 * contacts.getContacts helper: strip commas, parens, periods, quotes,
 * asterisks, and backslashes that have grammar meaning in `.or()`.
 */
export function sanitizeContentSearch(term: string): string {
  return term.replace(/[,()."*\\]/g, "").trim();
}

/**
 * Failure reason a content_item reference can produce. Mirrors the
 * backend `ContentResolutionErrorReason` from
 * supabase/functions/_shared/flightSendHelpers.ts so save-time
 * validation surfaces the SAME error vocabulary the send-path will.
 */
export type ContentResolutionErrorReason =
  | "missing"
  | "not_public"
  | "not_image"
  | "no_storage_url";

export interface ContentResolutionResult {
  resolved: Record<string, string>;
  errors: Array<{ contentId: string; reason: ContentResolutionErrorReason }>;
}

/**
 * Frontend-side replica of the backend loadContentUrls validation +
 * URL builder. Used by FlightForm save-time validation and by
 * TemplatePreviewModal to fetch image URLs for the preview render.
 *
 * v1 scope: IMAGE content items only, must be is_public=true, must
 * have content_images.storage_path. Same rules as send-path.
 */
export async function resolveContentUrlsForMappings(
  contentIds: string[],
): Promise<ContentResolutionResult> {
  const unique = Array.from(
    new Set(contentIds.filter((id) => typeof id === "string" && id.length > 0)),
  );
  if (unique.length === 0) {
    return { resolved: {}, errors: [] };
  }

  const { data: items, error: itemsErr } = await supabase
    .from("content_items")
    .select("id, is_public, content_type")
    .in("id", unique);
  if (itemsErr) {
    throw new Error(
      `resolveContentUrlsForMappings (items): ${itemsErr.message}`,
    );
  }
  const byId = new Map<
    string,
    { id: string; is_public: boolean | null; content_type: string }
  >();
  for (const row of (items ?? []) as Array<{
    id: string;
    is_public: boolean | null;
    content_type: string;
  }>) {
    byId.set(row.id, row);
  }

  const imageEligibleIds: string[] = [];
  for (const id of unique) {
    const it = byId.get(id);
    if (it && it.is_public === true && it.content_type === "IMAGE") {
      imageEligibleIds.push(id);
    }
  }
  const storagePaths = new Map<string, string>();
  if (imageEligibleIds.length > 0) {
    const { data: images, error: imgErr } = await supabase
      .from("content_images")
      .select("content_item_id, storage_path")
      .in("content_item_id", imageEligibleIds);
    if (imgErr) {
      throw new Error(
        `resolveContentUrlsForMappings (images): ${imgErr.message}`,
      );
    }
    for (const row of (images ?? []) as Array<{
      content_item_id: string;
      storage_path: string | null;
    }>) {
      if (row.storage_path && row.storage_path.length > 0) {
        storagePaths.set(row.content_item_id, row.storage_path);
      }
    }
  }

  // Build the storage public URL via the same helper the UI uses
  // elsewhere — keeps encoding consistent across surfaces.
  const supabaseBase = import.meta.env.VITE_SUPABASE_URL as string;
  const buildUrl = (storagePath: string) => {
    const encodedPath = storagePath
      .split("/")
      .map((seg) => encodeURIComponent(seg))
      .join("/");
    return `${supabaseBase}/storage/v1/object/public/${CONTENT_BUCKET}/${encodedPath}`;
  };

  const resolved: Record<string, string> = {};
  const errors: Array<{
    contentId: string;
    reason: ContentResolutionErrorReason;
  }> = [];
  for (const id of unique) {
    const it = byId.get(id);
    if (!it) {
      errors.push({ contentId: id, reason: "missing" });
      continue;
    }
    if (it.is_public !== true) {
      errors.push({ contentId: id, reason: "not_public" });
      continue;
    }
    if (it.content_type !== "IMAGE") {
      errors.push({ contentId: id, reason: "not_image" });
      continue;
    }
    const sp = storagePaths.get(id);
    if (!sp) {
      errors.push({ contentId: id, reason: "no_storage_url" });
      continue;
    }
    resolved[id] = buildUrl(sp);
  }

  return { resolved, errors };
}

/**
 * Human-readable label for a content resolution error. Used in
 * save-time validation messages.
 */
export function contentResolutionErrorLabel(
  reason: ContentResolutionErrorReason,
): string {
  switch (reason) {
    case "missing":
      return "Content Item not found";
    case "not_public":
      return "Content Item is not public";
    case "not_image":
      return "Only IMAGE Content Items are supported in template variables (v1)";
    case "no_storage_url":
      return "Content Item has no underlying image file";
  }
}

// =====================================================
// CONTENT ITEM CRUD
// =====================================================

/**
 * Get all content items with optional filtering
 */
export async function getContentItems(
  filters?: ContentFilters,
): Promise<ContentItem[]> {
  let query = supabase
    .from("content_items")
    .select(
      `
      *,
      creator:created_by (
        id,
        email,
        full_name
      )
    `,
    )
    .order("created_at", { ascending: false });

  // Apply filters
  if (filters?.content_type && filters.content_type.length > 0) {
    query = query.in("content_type", filters.content_type);
  }

  if (filters?.tags && filters.tags.length > 0) {
    // Filter by tags (array contains)
    query = query.contains("tags", filters.tags);
  }

  if (filters?.search) {
    // Sanitize before interpolating into PostgREST .or() — the same
    // allow-list pattern used by contacts.getContacts. Strips chars
    // that have special meaning in the .or() grammar (commas,
    // parens, periods, quotes, asterisks, backslashes) so operator
    // input can't break the predicate or inject extra branches.
    const safe = sanitizeContentSearch(filters.search);
    if (safe) {
      query = query.or(`title.ilike.*${safe}*,description.ilike.*${safe}*`);
    }
  }

  if (filters?.is_public !== undefined) {
    query = query.eq("is_public", filters.is_public);
  }

  if (filters?.organization_id) {
    query = query.eq("organization_id", filters.organization_id);
  }

  if (filters?.created_by) {
    query = query.eq("created_by", filters.created_by);
  }

  if (filters?.is_featured !== undefined) {
    query = query.eq("is_featured", filters.is_featured);
  }

  if (filters?.folder_id !== undefined) {
    if (filters.folder_id === null) {
      query = query.is("folder_id", null);
    } else {
      query = query.eq("folder_id", filters.folder_id);
    }
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching content items:", error);
    throw error;
  }

  // Add thumbnail URLs for each item
  const itemsWithThumbnails = (data || []).map((item) => ({
    ...item,
    thumbnail_url: item.thumbnail_url || generateThumbnailUrl(item),
  }));

  return itemsWithThumbnails;
}

/**
 * Get a single content item by ID with full details
 */
export async function getContentItem(id: UUID): Promise<ContentItem | null> {
  const { data: item, error } = await supabase
    .from("content_items")
    .select(
      `
      *,
      creator:created_by (
        id,
        email,
        full_name
      )
    `,
    )
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null; // Not found
    }
    console.error("Error fetching content item:", error);
    throw error;
  }

  // Fetch type-specific details
  const details = await getContentDetails(id, item.content_type);

  return {
    ...item,
    details,
    thumbnail_url: item.thumbnail_url || generateThumbnailUrl(item),
  };
}

/**
 * Get public content item (for 3rd party apps)
 * Only returns items marked as public
 */
export async function getPublicContentItem(
  id: UUID,
): Promise<ContentItem | null> {
  const { data: item, error } = await supabase
    .from("content_items")
    .select("*")
    .eq("id", id)
    .eq("is_public", true)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null;
    }
    throw error;
  }

  const details = await getContentDetails(id, item.content_type);

  return {
    ...item,
    details,
    thumbnail_url: item.thumbnail_url || generateThumbnailUrl(item),
  };
}

/**
 * Create a new content item
 */
export async function createContentItem(
  input: CreateContentItemInput,
  userId: UUID,
): Promise<ContentItem> {
  const { data, error } = await supabase
    .from("content_items")
    .insert({
      title: input.title,
      description: input.description,
      content_type: input.content_type,
      tags: input.tags || [],
      notes: input.notes,
      is_public: input.is_public || false,
      organization_id: input.organization_id,
      created_by: userId,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating content item:", error);
    throw error;
  }

  // Increment view count for analytics
  await incrementViewCount(data.id);

  return data;
}

/**
 * Update a content item
 */
export async function updateContentItem(
  id: UUID,
  input: UpdateContentItemInput,
  _userId: UUID,
): Promise<ContentItem> {
  const { data, error } = await supabase
    .from("content_items")
    .update({
      ...input,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating content item:", error);
    throw error;
  }

  return data;
}

/**
 * Delete a content item and all associated data
 */
export async function deleteContentItem(id: UUID): Promise<void> {
  // Get the item first to clean up storage
  const item = await getContentItem(id);
  if (!item) {
    return;
  }

  // Delete associated storage files based on type
  await cleanupContentStorage(id, item.content_type);

  // Delete the content item (cascades to type-specific tables)
  const { error } = await supabase.from("content_items").delete().eq("id", id);

  if (error) {
    console.error("Error deleting content item:", error);
    throw error;
  }
}

// =====================================================
// FLIPBOOK OPERATIONS
// =====================================================

/**
 * Create a flipbook from an uploaded PDF
 * Processes the PDF client-side and uploads page images
 */
export async function createFlipbook(
  file: File,
  title: string,
  userId: UUID,
  options?: {
    description?: string;
    tags?: string[];
    is_public?: boolean;
    organization_id?: UUID;
    onProgress?: (progress: PDFProcessingProgress) => void;
  },
): Promise<ContentItem> {
  const contentId = crypto.randomUUID();
  const storagePath = `flipbooks/${contentId}/original.pdf`;

  // Upload the original PDF to storage
  await uploadFile({
    provider: "supabase",
    bucket: CONTENT_BUCKET,
    path: storagePath,
    file,
    contentType: "application/pdf",
    upsert: true,
  });

  // Create the content item
  const { data: contentItem, error: contentError } = await supabase
    .from("content_items")
    .insert({
      id: contentId,
      title: title || file.name.replace(".pdf", ""),
      description: options?.description,
      content_type: "FLIPBOOK",
      tags: options?.tags || [],
      is_public: options?.is_public || false,
      organization_id: options?.organization_id,
      created_by: userId,
    })
    .select()
    .single();

  if (contentError) {
    // Clean up uploaded file on error
    await deleteFile("supabase", CONTENT_BUCKET, [storagePath]);
    throw contentError;
  }

  // Create the flipbook record with processing status
  const { error: flipbookError } = await supabase
    .from("content_flipbooks")
    .insert({
      content_item_id: contentId,
      page_count: 0,
      storage_path: storagePath,
      processing_status: "processing",
    });

  if (flipbookError) {
    await supabase.from("content_items").delete().eq("id", contentId);
    await deleteFile("supabase", CONTENT_BUCKET, [storagePath]);
    throw flipbookError;
  }

  // Process the PDF and upload pages (non-blocking for UI)
  processAndUploadFlipbookPages(contentId, file, options?.onProgress).catch(
    (error) => {
      console.error("Failed to process flipbook:", error);
      // Update status to failed
      supabase
        .from("content_flipbooks")
        .update({ processing_status: "failed" })
        .eq("content_item_id", contentId);
    },
  );

  return contentItem;
}

/**
 * Process PDF pages and upload them to storage
 * This runs in the background after the content item is created
 */
async function processAndUploadFlipbookPages(
  contentId: UUID,
  file: File,
  onProgress?: (progress: PDFProcessingProgress) => void,
): Promise<void> {
  try {
    // Process the PDF to get page images
    const result = await processPDF(file, onProgress);
    const pageUrls: string[] = [];

    // Upload each page
    for (const page of result.pages) {
      const pageNum = String(page.pageNumber).padStart(3, "0");
      const pagePath = `flipbooks/${contentId}/pages/page-${pageNum}.webp`;

      await uploadFile({
        provider: "supabase",
        bucket: CONTENT_BUCKET,
        path: pagePath,
        file: page.blob,
        contentType: "image/webp",
        upsert: true,
      });

      pageUrls.push(getPublicUrl("supabase", CONTENT_BUCKET, pagePath));
    }

    // Get thumbnail URL (first page)
    const thumbnailPath = `flipbooks/${contentId}/pages/page-001.webp`;
    const thumbnailUrl = getPublicUrl(
      "supabase",
      CONTENT_BUCKET,
      thumbnailPath,
    );

    // Update flipbook record with page count and ready status
    await supabase
      .from("content_flipbooks")
      .update({
        page_count: result.totalPages,
        page_urls: pageUrls,
        processing_status: "ready",
        processed_at: new Date().toISOString(),
      })
      .eq("content_item_id", contentId);

    // Update thumbnail on content item
    await supabase
      .from("content_items")
      .update({ thumbnail_url: thumbnailUrl })
      .eq("id", contentId);
  } catch (error) {
    // Update status to failed
    await supabase
      .from("content_flipbooks")
      .update({ processing_status: "failed" })
      .eq("content_item_id", contentId);
    throw error;
  }
}

/**
 * Get flipbook viewer data for displaying in the flipbook viewer
 */
export async function getFlipbookViewerData(
  contentItemId: UUID,
): Promise<FlipbookViewerData | null> {
  const { data: flipbook, error } = await supabase
    .from("content_flipbooks")
    .select("*")
    .eq("content_item_id", contentItemId)
    .single();

  if (error || !flipbook) {
    return null;
  }

  // Check if processing is complete
  if (flipbook.processing_status !== "ready") {
    return {
      totalPages: 0,
      pdfDownloadUrl: flipbook.storage_path
        ? getPublicUrl("supabase", CONTENT_BUCKET, flipbook.storage_path)
        : "",
      html: "",
      pages: [],
      processingStatus: flipbook.processing_status,
    };
  }

  // Use stored page URLs if available, otherwise generate them
  const pages: FlipbookPage[] = [];
  const storedUrls = flipbook.page_urls as string[] | null;

  if (storedUrls && storedUrls.length > 0) {
    storedUrls.forEach((url: string, index: number) => {
      pages.push({
        pageNumber: index + 1,
        imageUrl: url,
      });
    });
  } else {
    // Fallback: generate URLs from page count
    for (let i = 1; i <= flipbook.page_count; i++) {
      const pageNum = String(i).padStart(3, "0");
      const pagePath = `flipbooks/${contentItemId}/pages/page-${pageNum}.webp`;
      pages.push({
        pageNumber: i,
        imageUrl: getPublicUrl("supabase", CONTENT_BUCKET, pagePath),
      });
    }
  }

  // Generate HTML for the viewer (for legacy compatibility)
  const html = pages
    .map(
      (page) =>
        `<div class="page" data-page="${page.pageNumber}"><img src="${page.imageUrl}" alt="Page ${page.pageNumber}" loading="lazy" /></div>`,
    )
    .join("");

  return {
    totalPages: pages.length || flipbook.page_count,
    pdfDownloadUrl: flipbook.storage_path
      ? getPublicUrl("supabase", CONTENT_BUCKET, flipbook.storage_path)
      : "",
    html: `<div id="flipbook-container">${html}</div>`,
    pages,
    processingStatus: flipbook.processing_status,
  };
}

/**
 * Update flipbook processing status (called by Edge Function)
 */
export async function updateFlipbookProcessingStatus(
  contentItemId: UUID,
  status: "processing" | "ready" | "failed",
  pageCount?: number,
): Promise<void> {
  const updates: Record<string, unknown> = {
    processing_status: status,
  };

  if (status === "ready" && pageCount !== undefined) {
    updates.page_count = pageCount;
    updates.processed_at = new Date().toISOString();
  }

  const { error } = await supabase
    .from("content_flipbooks")
    .update(updates)
    .eq("content_item_id", contentItemId);

  if (error) {
    console.error("Error updating flipbook status:", error);
    throw error;
  }

  // Update thumbnail URL on the content item
  if (status === "ready") {
    const thumbnailPath = `flipbooks/${contentItemId}/pages/page-001.webp`;
    const thumbnailUrl = getPublicUrl(
      "supabase",
      CONTENT_BUCKET,
      thumbnailPath,
    );

    await supabase
      .from("content_items")
      .update({ thumbnail_url: thumbnailUrl })
      .eq("id", contentItemId);
  }
}

// =====================================================
// IMAGE OPERATIONS
// =====================================================

/**
 * Create an image content item
 */
export async function createImage(
  file: File,
  title: string,
  userId: UUID,
  options?: {
    description?: string;
    tags?: string[];
    alt_text?: string;
    is_public?: boolean;
    organization_id?: UUID;
  },
): Promise<ContentItem> {
  const contentId = crypto.randomUUID();
  const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const storagePath = `images/${contentId}/original.${ext}`;

  // Upload the image
  await uploadFile({
    provider: "supabase",
    bucket: CONTENT_BUCKET,
    path: storagePath,
    file,
    contentType: file.type,
    upsert: true,
  });

  // Get image dimensions (client-side)
  const dimensions = await getImageDimensions(file);

  // Create the content item
  const { data: contentItem, error: contentError } = await supabase
    .from("content_items")
    .insert({
      id: contentId,
      title: title || file.name,
      description: options?.description,
      content_type: "IMAGE",
      tags: options?.tags || [],
      is_public: options?.is_public || false,
      organization_id: options?.organization_id,
      thumbnail_url: getPublicUrl("supabase", CONTENT_BUCKET, storagePath),
      created_by: userId,
    })
    .select()
    .single();

  if (contentError) {
    await deleteFile("supabase", CONTENT_BUCKET, [storagePath]);
    throw contentError;
  }

  // Create the image details record
  const { error: imageError } = await supabase.from("content_images").insert({
    content_item_id: contentId,
    storage_path: storagePath,
    alt_text: options?.alt_text,
    width: dimensions.width,
    height: dimensions.height,
    file_size: file.size,
    mime_type: file.type,
  });

  if (imageError) {
    await supabase.from("content_items").delete().eq("id", contentId);
    await deleteFile("supabase", CONTENT_BUCKET, [storagePath]);
    throw imageError;
  }

  return contentItem;
}

// =====================================================
// VIDEO LINK OPERATIONS
// =====================================================

/**
 * Create a video link content item
 */
export async function createVideoLink(
  url: string,
  title: string,
  userId: UUID,
  options?: {
    description?: string;
    tags?: string[];
    is_public?: boolean;
    organization_id?: UUID;
  },
): Promise<ContentItem> {
  const contentId = crypto.randomUUID();

  // Parse video URL to extract platform and video ID
  const videoInfo = parseVideoUrl(url);

  // Create the content item
  const { data: contentItem, error: contentError } = await supabase
    .from("content_items")
    .insert({
      id: contentId,
      title,
      description: options?.description,
      content_type: "VIDEO_LINK",
      tags: options?.tags || [],
      is_public: options?.is_public || false,
      organization_id: options?.organization_id,
      thumbnail_url: videoInfo.thumbnailUrl,
      created_by: userId,
    })
    .select()
    .single();

  if (contentError) {
    throw contentError;
  }

  // Create the video link record
  const { error: videoError } = await supabase.from("content_videos").insert({
    content_item_id: contentId,
    url,
    platform: videoInfo.platform,
    video_id: videoInfo.videoId,
    thumbnail_url: videoInfo.thumbnailUrl,
  });

  if (videoError) {
    await supabase.from("content_items").delete().eq("id", contentId);
    throw videoError;
  }

  return contentItem;
}

// =====================================================
// FILE OPERATIONS (Generic downloadable files)
// =====================================================

/**
 * Create a file content item (PDFs, docs, etc. without flipbook conversion)
 */
export async function createFile(
  file: File,
  title: string,
  userId: UUID,
  options?: {
    description?: string;
    tags?: string[];
    is_public?: boolean;
    organization_id?: UUID;
  },
): Promise<ContentItem> {
  const contentId = crypto.randomUUID();
  const storagePath = `files/${contentId}/${file.name}`;

  // Upload the file
  await uploadFile({
    provider: "supabase",
    bucket: CONTENT_BUCKET,
    path: storagePath,
    file,
    contentType: file.type,
    upsert: true,
  });

  // Create the content item
  const { data: contentItem, error: contentError } = await supabase
    .from("content_items")
    .insert({
      id: contentId,
      title: title || file.name,
      description: options?.description,
      content_type: "FILE",
      tags: options?.tags || [],
      is_public: options?.is_public || false,
      organization_id: options?.organization_id,
      created_by: userId,
    })
    .select()
    .single();

  if (contentError) {
    await deleteFile("supabase", CONTENT_BUCKET, [storagePath]);
    throw contentError;
  }

  // Create the file details record
  const { error: fileError } = await supabase.from("content_files").insert({
    content_item_id: contentId,
    storage_path: storagePath,
    file_name: file.name,
    file_size: file.size,
    mime_type: file.type,
  });

  if (fileError) {
    await supabase.from("content_items").delete().eq("id", contentId);
    await deleteFile("supabase", CONTENT_BUCKET, [storagePath]);
    throw fileError;
  }

  return contentItem;
}

// =====================================================
// FORMATTED TEXT (TEXT_BLOCK) OPERATIONS
// =====================================================

/**
 * Create a formatted text content item
 */
export async function createFormattedText(
  title: string,
  content: string,
  userId: UUID,
  options?: {
    description?: string;
    tags?: string[];
    is_public?: boolean;
    organization_id?: UUID;
  },
): Promise<ContentItem> {
  const contentId = crypto.randomUUID();

  // Create the content item
  const { data: contentItem, error: contentError } = await supabase
    .from("content_items")
    .insert({
      id: contentId,
      title,
      description: options?.description,
      content_type: "TEXT_BLOCK",
      tags: options?.tags || [],
      is_public: options?.is_public || false,
      organization_id: options?.organization_id,
      created_by: userId,
    })
    .select()
    .single();

  if (contentError) {
    throw contentError;
  }

  // Create the text block details record
  const { error: textError } = await supabase
    .from("content_text_blocks")
    .insert({
      content_item_id: contentId,
      content,
      content_format: "html",
    });

  if (textError) {
    await supabase.from("content_items").delete().eq("id", contentId);
    throw textError;
  }

  return contentItem;
}

/**
 * Update the content of a formatted text item
 */
export async function updateFormattedText(
  contentItemId: UUID,
  content: string,
): Promise<void> {
  const { error } = await supabase
    .from("content_text_blocks")
    .update({ content, content_format: "html" })
    .eq("content_item_id", contentItemId);

  if (error) {
    console.error("Error updating formatted text:", error);
    throw error;
  }
}

// =====================================================
// ANALYTICS
// =====================================================

/**
 * Increment view count for a content item
 */
export async function incrementViewCount(id: UUID): Promise<void> {
  await supabase.rpc("increment_content_view_count", { content_id: id });
}

/**
 * Increment download count for a content item
 */
export async function incrementDownloadCount(id: UUID): Promise<void> {
  await supabase.rpc("increment_content_download_count", { content_id: id });
}

// =====================================================
// USAGE TRACKING
// =====================================================

/**
 * Track where content is being used (events, campaigns, etc.)
 */
export async function trackContentUsage(
  contentItemId: UUID,
  entityType: string,
  entityId: UUID,
): Promise<void> {
  const { error } = await supabase.from("content_usage").upsert(
    {
      content_item_id: contentItemId,
      entity_type: entityType,
      entity_id: entityId,
    },
    {
      onConflict: "content_item_id,entity_type,entity_id",
    },
  );

  if (error) {
    console.error("Error tracking content usage:", error);
  }
}

/**
 * Get all places where content is used
 */
export async function getContentUsage(
  contentItemId: UUID,
): Promise<Array<{ entity_type: string; entity_id: UUID }>> {
  const { data, error } = await supabase
    .from("content_usage")
    .select("entity_type, entity_id")
    .eq("content_item_id", contentItemId);

  if (error) {
    console.error("Error fetching content usage:", error);
    return [];
  }

  return data || [];
}

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Get type-specific details for a content item
 */
async function getContentDetails(
  id: UUID,
  contentType: ContentType,
): Promise<
  | FlipbookDetails
  | ImageDetails
  | VideoLinkDetails
  | TextBlockDetails
  | WebpageChatDetails
  | null
> {
  switch (contentType) {
    case "FLIPBOOK": {
      const { data } = await supabase
        .from("content_flipbooks")
        .select("*")
        .eq("content_item_id", id)
        .single();

      if (data) {
        // Use page_urls array length as source of truth for page count
        const pageUrls = data.page_urls as string[] | null;
        const actualPageCount = pageUrls?.length || data.page_count || 0;

        return {
          ...data,
          page_count: actualPageCount,
          publicUrl: getPublicUrl(
            "supabase",
            CONTENT_BUCKET,
            data.storage_path,
          ),
        };
      }
      return null;
    }

    case "IMAGE": {
      const { data } = await supabase
        .from("content_images")
        .select("*")
        .eq("content_item_id", id)
        .single();

      if (data) {
        return {
          ...data,
          publicUrl: getPublicUrl(
            "supabase",
            CONTENT_BUCKET,
            data.storage_path,
          ),
        };
      }
      return null;
    }

    case "VIDEO_LINK": {
      const { data } = await supabase
        .from("content_videos")
        .select("*")
        .eq("content_item_id", id)
        .single();

      return data || null;
    }

    case "FILE": {
      const { data } = await supabase
        .from("content_files")
        .select("*")
        .eq("content_item_id", id)
        .single();

      if (data) {
        return {
          ...data,
          publicUrl: getPublicUrl(
            "supabase",
            CONTENT_BUCKET,
            data.storage_path,
          ),
        };
      }
      return null;
    }

    case "TEXT_BLOCK": {
      const { data } = await supabase
        .from("content_text_blocks")
        .select("*")
        .eq("content_item_id", id)
        .single();

      return data as TextBlockDetails | null;
    }

    case "WEBPAGE_CHAT": {
      const { data } = await supabase
        .from("content_webpage_chats")
        .select("*")
        .eq("content_item_id", id)
        .single();

      return data as WebpageChatDetails | null;
    }

    default:
      return null;
  }
}

/**
 * Generate thumbnail URL based on content type
 */
function generateThumbnailUrl(_item: ContentItem): string | undefined {
  // Thumbnail generation logic would go here
  // For now, return undefined and let the UI show a placeholder
  return undefined;
}

/**
 * Clean up storage files when deleting content
 */
async function cleanupContentStorage(
  id: UUID,
  contentType: ContentType,
): Promise<void> {
  const basePath = getStorageBasePath(id, contentType);

  try {
    // List all files in the content's folder
    const { data: files } = await supabase.storage
      .from(CONTENT_BUCKET)
      .list(basePath);

    if (files && files.length > 0) {
      const paths = files.map((f) => `${basePath}/${f.name}`);
      await deleteFile("supabase", CONTENT_BUCKET, paths);
    }
  } catch (error) {
    console.error("Error cleaning up storage:", error);
  }
}

/**
 * Get base storage path for content type
 */
function getStorageBasePath(id: UUID, contentType: ContentType): string {
  switch (contentType) {
    case "FLIPBOOK":
      return `flipbooks/${id}`;
    case "IMAGE":
      return `images/${id}`;
    case "FILE":
      return `files/${id}`;
    default:
      return `other/${id}`;
  }
}

/**
 * Get image dimensions from a file
 */
async function getImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Parse video URL to extract platform and video ID
 */
function parseVideoUrl(url: string): {
  platform: VideoPlatform;
  videoId: string;
  thumbnailUrl?: string;
} {
  const urlLower = url.toLowerCase();

  // YouTube
  if (urlLower.includes("youtube.com") || urlLower.includes("youtu.be")) {
    const videoId = extractYouTubeId(url);
    return {
      platform: "YOUTUBE" as VideoPlatform,
      videoId: videoId || "",
      thumbnailUrl: videoId
        ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
        : undefined,
    };
  }

  // Vimeo
  if (urlLower.includes("vimeo.com")) {
    const videoId = extractVimeoId(url);
    return {
      platform: "VIMEO" as VideoPlatform,
      videoId: videoId || "",
      // Vimeo thumbnails require API call, so we leave it undefined
      thumbnailUrl: undefined,
    };
  }

  // Wistia
  if (urlLower.includes("wistia.com") || urlLower.includes("wistia.net")) {
    const videoId = extractWistiaId(url);
    return {
      platform: "WISTIA" as VideoPlatform,
      videoId: videoId || "",
      thumbnailUrl: undefined,
    };
  }

  // Other
  return {
    platform: "OTHER" as VideoPlatform,
    videoId: "",
    thumbnailUrl: undefined,
  };
}

/**
 * Extract YouTube video ID from URL
 */
function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * Extract Vimeo video ID from URL
 */
function extractVimeoId(url: string): string | null {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match ? match[1] : null;
}

/**
 * Extract Wistia video ID from URL
 */
function extractWistiaId(url: string): string | null {
  const match = url.match(
    /wistia\.(?:com|net)\/(?:medias|embed)\/([a-zA-Z0-9]+)/,
  );
  return match ? match[1] : null;
}

// =====================================================
// WEBPAGE CHAT OPERATIONS
// =====================================================

/**
 * Create a webpage chat content item
 */
export async function createWebpageChat(
  sourceUrl: string,
  title: string,
  userId: UUID,
  options?: {
    description?: string;
    tags?: string[];
    is_public?: boolean;
    allowed_domains?: string[];
    organization_id?: UUID;
  },
): Promise<ContentItem> {
  const contentId = crypto.randomUUID();

  // Create the content item
  const { data: contentItem, error: contentError } = await supabase
    .from("content_items")
    .insert({
      id: contentId,
      title,
      description: options?.description,
      content_type: "WEBPAGE_CHAT",
      tags: options?.tags || [],
      is_public: options?.is_public || false,
      organization_id: options?.organization_id,
      created_by: userId,
      chat_enabled: true, // Chat is always enabled for webpage chat
    })
    .select()
    .single();

  if (contentError) {
    throw contentError;
  }

  // Create the webpage chat details record
  const { error: webpageError } = await supabase
    .from("content_webpage_chats")
    .insert({
      content_item_id: contentId,
      source_url: sourceUrl,
      allowed_domains: options?.allowed_domains || [],
      crawl_status: "pending",
    });

  if (webpageError) {
    await supabase.from("content_items").delete().eq("id", contentId);
    throw webpageError;
  }

  return contentItem;
}

/**
 * Trigger webpage crawling via edge function
 */
export async function triggerWebpageCrawl(contentItemId: UUID): Promise<void> {
  const { error } = await supabase.functions.invoke("crawl-webpage", {
    body: { contentItemId },
  });

  if (error) {
    console.error("Error triggering webpage crawl:", error);
    throw error;
  }
}

/**
 * Update allowed domains for a webpage chat
 */
export async function updateWebpageChatDomains(
  contentItemId: UUID,
  allowedDomains: string[],
): Promise<void> {
  const { error } = await supabase
    .from("content_webpage_chats")
    .update({
      allowed_domains: allowedDomains,
    })
    .eq("content_item_id", contentItemId);

  if (error) {
    console.error("Error updating webpage chat domains:", error);
    throw error;
  }
}

/**
 * Get webpage chat details
 */
export async function getWebpageChatDetails(contentItemId: UUID): Promise<{
  source_url: string;
  allowed_domains: string[];
  crawl_status: string;
} | null> {
  const { data, error } = await supabase
    .from("content_webpage_chats")
    .select("source_url, allowed_domains, crawl_status")
    .eq("content_item_id", contentItemId)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null;
    }
    throw error;
  }

  return data;
}

// =====================================================
// BULK OPERATIONS
// =====================================================

/**
 * Bulk delete content items
 */
export async function bulkDeleteContentItems(ids: UUID[]): Promise<void> {
  // Get all items to clean up storage
  for (const id of ids) {
    await deleteContentItem(id);
  }
}

/**
 * Bulk update tags on content items
 */
export async function bulkUpdateTags(
  ids: UUID[],
  tags: string[],
  mode: "add" | "replace",
): Promise<void> {
  for (const id of ids) {
    if (mode === "replace") {
      await supabase
        .from("content_items")
        .update({ tags, updated_at: new Date().toISOString() })
        .eq("id", id);
    } else {
      // Add tags - need to fetch current tags first
      const { data: item } = await supabase
        .from("content_items")
        .select("tags")
        .eq("id", id)
        .single();

      if (item) {
        const currentTags = item.tags || [];
        const newTags = [...new Set([...currentTags, ...tags])];
        await supabase
          .from("content_items")
          .update({ tags: newTags, updated_at: new Date().toISOString() })
          .eq("id", id);
      }
    }
  }
}

/**
 * Bulk update visibility on content items
 */
export async function bulkUpdateVisibility(
  ids: UUID[],
  isPublic: boolean,
): Promise<void> {
  const { error } = await supabase
    .from("content_items")
    .update({ is_public: isPublic, updated_at: new Date().toISOString() })
    .in("id", ids);

  if (error) {
    console.error("Error updating visibility:", error);
    throw error;
  }
}
